"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, TrendingUp, Calendar, ArrowRight, ShieldCheck, UserCheck, Search, Star } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const trustPoints = [{
  text: "100% Direct Access to Yassine",
  icon: UserCheck
}, {
  text: "Tailored Wolverhampton SEO Strategy",
  icon: MapPin
}, {
  text: "Google Business Profile & Map Pack Growth",
  icon: Search
}, {
  text: "Transparent Reporting & No Middlemen",
  icon: ShieldCheck
}];
const kpiBadges = [{
  label: "Local Visibility",
  value: "+310%",
  icon: TrendingUp
}, {
  label: "5-Star Local Rating",
  value: "5.0",
  icon: Star
}];
export default function LocalHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="localseohero" data-nav="dark" className="relative overflow-hidden bg-primary text-primary-foreground py-16 md:py-24 lg:py-32">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center" variants={containerVariants} initial="hidden" animate="visible">
          <div className="lg:col-span-7 space-y-6 md:space-y-8">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="bg-accent text-accent-foreground px-3 py-1 text-xs sm:text-sm font-semibold rounded-full border-none shadow-sm inline-flex items-center gap-1.5">
                <MapPin className="size-3.5 text-accent-foreground" />
                <span>Wolverhampton &amp; West Midlands</span>
              </Badge>
              <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground px-3 py-1 text-xs sm:text-sm font-medium rounded-full">
                Direct Freelance SEO Consultant
              </Badge>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-primary-foreground leading-[1.15]">
                Local SEO Consultant in Wolverhampton
              </h1>
              <p className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl font-normal leading-relaxed">
                I help Wolverhampton and West Midlands businesses capture high-intent local search demand, dominate the Google Map Pack, and turn organic search traffic into direct enquiries. When you work with YE Consultancy, you work directly with me—Yassine Essaadi—no account managers, no generic agency templates.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {trustPoints.map((point, i) => {
              const IconComponent = point.icon;
              return <div data-index={i} key={point.text} className="flex items-center gap-2.5 text-xs sm:text-sm text-primary-foreground/95 bg-primary-foreground/10 backdrop-blur-xs p-2.5 rounded-lg border border-primary-foreground/15">
                    <IconComponent className="size-4 shrink-0 text-accent" />
                    <span className="font-medium">{point.text}</span>
                  </div>;
            })}
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6 py-6 text-base rounded-lg shadow-lg shadow-accent/20 transition-all cursor-pointer">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-medium px-6 py-6 text-base rounded-lg transition-all">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-2 flex items-center gap-3 border-t border-primary-foreground/15">
              <div className="flex -space-x-2 overflow-hidden">
                <SafeHydrate><Avatar className="inline-block border-2 border-primary size-9">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_1.png" alt="Wolverhampton Client" />
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs font-bold">YE</AvatarFallback>
                </Avatar></SafeHydrate>
                <SafeHydrate><Avatar className="inline-block border-2 border-primary size-9">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_2.png" alt="Local Business Client" />
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs font-bold">UK</AvatarFallback>
                </Avatar></SafeHydrate>
                <SafeHydrate><Avatar className="inline-block border-2 border-primary size-9">
                  <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/client_avatar_3.webp" alt="B2B Client" />
                  <AvatarFallback className="bg-accent text-accent-foreground text-xs font-bold">SEO</AvatarFallback>
                </Avatar></SafeHydrate>
              </div>
              <p className="text-xs sm:text-sm text-primary-foreground/85 font-sans">
                Trusted by local businesses across Wolverhampton, Black Country &amp; West Midlands.
              </p>
            </motion.div>
          </div>

          <motion.div variants={itemVariants} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="absolute -inset-1.5 rounded-2xl bg-gradient-to-tr from-accent to-primary-foreground/30 opacity-70 blur-sm" />
              
              <div className="relative rounded-xl bg-card text-card-foreground p-6 shadow-2xl border border-border">
                <div className="flex items-center gap-4 pb-6 border-b border-border">
                  <div className="relative shrink-0">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Freelance Local SEO Consultant in Wolverhampton" loading="lazy" decoding="async" className="size-20 rounded-lg object-cover shadow-md border border-border" />
                    <span className="absolute -bottom-1 -right-1 bg-accent text-accent-foreground p-1 rounded-full shadow-xs">
                      <CheckCircle2 className="size-3.5" />
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-serif font-bold text-foreground">Yassine Essaadi</h2>
                    <p className="text-xs font-sans text-muted-foreground font-medium">Independent SEO Consultant</p>
                    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="size-3 text-primary" />
                      <span>Serving Wolverhampton &amp; UK</span>
                    </div>
                  </div>
                </div>

                <div className="py-6 space-y-4">
                  <h3 className="text-xs font-serif font-semibold text-foreground uppercase tracking-wider">
                    My Hands-On Local SEO Framework
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-2.5 rounded-md bg-muted/60 border border-border/50">
                      <div className="p-1.5 rounded-sm bg-primary/10 text-primary shrink-0 mt-0.5">
                        <Search className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Google Map Pack Optimisation</p>
                        <p className="text-xs text-muted-foreground">Claiming local 3-pack rankings for relevant search queries.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-2.5 rounded-md bg-muted/60 border border-border/50">
                      <div className="p-1.5 rounded-sm bg-primary/10 text-primary shrink-0 mt-0.5">
                        <MapPin className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Localized Content &amp; Citations</p>
                        <p className="text-xs text-muted-foreground">Building location authority without artificial link schemes.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-2.5 rounded-md bg-muted/60 border border-border/50">
                      <div className="p-1.5 rounded-sm bg-primary/10 text-primary shrink-0 mt-0.5">
                        <TrendingUp className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground">Conversion-Focused Traffic</p>
                        <p className="text-xs text-muted-foreground">Turning local organic visitors into calls and consultation bookings.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border">
                  {kpiBadges.map((kpi, i) => {
                  const KpiIcon = kpi.icon;
                  return <div data-index={i} key={kpi.label} className="p-3 rounded-lg bg-primary/5 border border-primary/10 text-center">
                        <div className="flex items-center justify-center gap-1 text-primary mb-1">
                          <KpiIcon className="size-4" />
                          <span className="text-lg font-serif font-bold">{kpi.value}</span>
                        </div>
                        <p className="text-[13px] font-sans text-muted-foreground font-medium">{kpi.label}</p>
                      </div>;
                })}
                </div>

                <div className="mt-5 pt-4 text-center border-t border-border/60">
                  <Button asChild variant="default" size="sm" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-xs rounded-md py-2.5">
                    <Link to="/LocalSEOPage" className="inline-flex items-center justify-center gap-1.5">
                      <span>Learn More About Local SEO Services</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </div>

              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}